import React, { useRef, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";

import { useDetectOnline } from "./useDetectOnline";
import {
  showSnackbar,
  setEnabledGlobalLoading,
} from "redux/slices/common/commonSlice";
import Toast from "components/ui/Toast";

const BASE_URL = process.env.REACT_APP_API as string;
const API_KEY = process.env.REACT_APP_API_KEY as string;
const VERSION_CHECK_END_POINT = `${BASE_URL}/version/webview`;

// globalThis에 appVersion 속성 추가
declare global {
  var appVersion: string;
}

const useInitWebviewVersion = () => {
  const isProcessDone = useRef(false);

  const initNowAppVersion = async () => {
    if (isProcessDone.current) return;

    try {
      const res = await axios.get(VERSION_CHECK_END_POINT, {
        withCredentials: true,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-API-KEY": API_KEY,
        },
      });
      const version = res.data.version;
      globalThis.appVersion = version;
      isProcessDone.current = true;
    } catch (error) {
      console.error("Failed to fetch app version:", error);
    }
  };

  useEffect(() => {
    initNowAppVersion();
  }, []);
};

export const useLatestWebviewVersion = () => {
  useInitWebviewVersion();
  const { isOffline, runIfOnline } = useDetectOnline();
  const dispatch = useDispatch();

  const reloadIfOnline = () => {
    dispatch(setEnabledGlobalLoading(true));
    runIfOnline({
      onNowOnline: () => {
        window.location.reload();
      },
      onNowOffline: () => {
        Toast(`"인터넷 연결이 불안정해요.`, "", "76px");
      },
    });
  };

  const checkCurrentWebAppVersion = useCallback(async () => {
    try {
      const res = await axios.get(VERSION_CHECK_END_POINT, {
        withCredentials: true,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-API-KEY": API_KEY,
        },
      });
      const version = res.data.version;
      const isChanged = version !== globalThis.appVersion;

      if (isChanged) {
        dispatch(
          showSnackbar({
            type: "default",
            messageKey: "webviewChanged",
            actionLabel: "새로고침",
            // onAction: reloadIfOnline,
          })
        );
      }
    } catch (error) {
      console.log("버전 체크 중 에러가 발생했어요", error);
    }
  }, [dispatch]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkCurrentWebAppVersion();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkCurrentWebAppVersion]);

  return { isOffline, checkCurrentWebAppVersion, reloadIfOnline };
};
